/**
 * Household Expenditure Measure (HEM) Benchmarks
 *
 * Based on Melbourne Institute HEM data, adjusted for 2026.
 * These are monthly living expense estimates by household type.
 *
 * Note: HEM excludes rent/mortgage payments.
 * Lenders use the higher of declared expenses or HEM.
 */

export type HouseholdType = "single" | "couple";
export type Location = "metro" | "regional";

interface HEMBenchmark {
  single: number;
  couple: number;
  perChild: number;
}

/**
 * Monthly HEM benchmarks by location (2026 estimates)
 * Based on ABS Household Expenditure Survey data
 */
export const hemBenchmarks: Record<Location, HEMBenchmark> = {
  metro: {
    single: 1650,    // ~$19,800/year
    couple: 2550,    // ~$30,600/year
    perChild: 450,   // ~$5,400/year per dependent
  },
  regional: {
    single: 1450,    // ~$17,400/year
    couple: 2250,    // ~$27,000/year
    perChild: 400,   // ~$4,800/year per dependent
  },
};

/**
 * Calculate monthly HEM living expenses
 */
export function calculateHEM(
  householdType: HouseholdType,
  dependents: number,
  location: Location
): number {
  const benchmark = hemBenchmarks[location];
  const base = householdType === "single" ? benchmark.single : benchmark.couple;
  const childExpenses = dependents * benchmark.perChild;
  return base + childExpenses;
}

/**
 * State capital classification for HEM
 */
export const stateLocations: Record<string, Location> = {
  "Sydney": "metro",
  "Melbourne": "metro",
  "Brisbane": "metro",
  "Perth": "metro",
  "Adelaide": "metro",
  "Hobart": "regional",
  "Darwin": "regional",
  "Canberra": "metro",
  "Regional": "regional",
};

/**
 * Credit card assessment rate (3% of limit per month)
 * This is standard across most Australian lenders
 */
export const CREDIT_CARD_ASSESSMENT_RATE = 0.03;

/**
 * APRA serviceability buffer (added to advertised rate)
 * Currently 3% as mandated by APRA
 */
export const APRA_BUFFER = 0.03;

/**
 * Default assessment interest rate if not specified
 * Based on current average variable rate + buffer
 */
export const DEFAULT_ASSESSMENT_RATE = 0.09; // 9% (6% + 3% buffer)
